import { useEffect, useMemo, useState } from 'react'

import { Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import {
  ArrowClockwise,
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  TrashFill,
} from 'react-bootstrap-icons'
import { toast } from 'react-toastify'

import { CustomButton } from '../../../components/CustomButton'

import SubcategoriesService from '../../../services/database/SubcategoriesService'

import { useOrderTable } from '../../../hooks/useOrderTable'

import { sendErrorApiMessage } from '../../../utils/sendErrorApiMessage'

import { Center, Container, GroupX } from './styles'
import { CustomSpinner } from '../../../components/CustomSpinner'
import { ModalCreate } from './ModalCreate'
import { ModalUpdate } from './ModalUpdate'

export function Subcategories() {
  const [subcategories, setSubcategories] = useState([])
  const [isSubcategoriesLoading, setIsSubcategoriesLoading] = useState(true)

  const [subcategorySearch, setSubcategorySearch] = useState('')

  const [isModalCreateOpen, setIsModalCreateOpen] = useState(false)

  const filteredSubcategories = useMemo(() => {
    return subcategories.filter((subcategory) =>
      subcategory.subcategoria
        .toLowerCase()
        .includes(subcategorySearch.toLowerCase()),
    )
  }, [subcategories, subcategorySearch])

  const {
    check,
    handleOrganize,
    sortedArray: sortedSubcategories,
  } = useOrderTable(filteredSubcategories, 'subcategoria', 'asc')

  async function loadSubcategories() {
    try {
      setIsSubcategoriesLoading(true)

      const data = await SubcategoriesService.getAll()

      setSubcategories(data)
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao carregar as subcategorias!')
    } finally {
      setIsSubcategoriesLoading(false)
    }
  }

  useEffect(() => {
    loadSubcategories()
  }, [])

  return (
    <>
      <Container>
        <h1>Subcategorias</h1>

        {isSubcategoriesLoading ? (
          <Center>
            <CustomSpinner size={48} />

            <h1>Carregando...</h1>
          </Center>
        ) : (
          <>
            <GroupX>
              <CustomButton
                title="Cadastrar Subcategoria"
                color="#0d9a04"
                onClick={() => setIsModalCreateOpen(true)}
                styles={{
                  marginLeft: 0,
                }}
              >
                <PlusCircleFill />
              </CustomButton>

              <CustomButton
                title="Recarregar"
                color="#04519a"
                onClick={loadSubcategories}
                styles={{
                  marginLeft: 0,
                }}
              >
                <ArrowClockwise />
              </CustomButton>
            </GroupX>

            <Form.Control
              value={subcategorySearch}
              onChange={(e) => setSubcategorySearch(e.target.value)}
              placeholder="Pesquise..."
              style={{
                marginTop: 20,
              }}
            />

            <Table>
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('id_subcategoria')}>
                    <span>ID</span>
                    <CaretUp color={check('id_subcategoria', 'asc')} />
                    <CaretDown color={check('id_subcategoria', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('subcategoria')}>
                    <span>Nome</span>
                    <CaretUp color={check('subcategoria', 'asc')} />
                    <CaretDown color={check('subcategoria', 'desc')} />
                  </th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedSubcategories.map((subcategory) => (
                  <Subcategory
                    key={subcategory.id_subcategoria}
                    subcategory={subcategory}
                    loadSubcategories={loadSubcategories}
                  />
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Container>

      {isModalCreateOpen && (
        <ModalCreate
          isOpen={isModalCreateOpen}
          onClose={() => setIsModalCreateOpen(false)}
          loadSubcategories={loadSubcategories}
        />
      )}
    </>
  )
}

function Subcategory({ subcategory, loadSubcategories }) {
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState()

  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState(false)
  const [isModalUpdateOpen, setIsModalUpdateOpen] = useState(false)

  async function handleDeleteSubcategory(id) {
    try {
      setIsDeleteRequestLoading(true)

      await SubcategoriesService.delete(id)

      loadSubcategories()
      toast.success('Subcategoria deletada com sucesso!')
    } catch (err) {
      sendErrorApiMessage(err, 'Erro ao deletar a subcategoria!')
    } finally {
      setIsDeleteRequestLoading(false)
    }
  }

  return (
    <>
      <tr>
        <td>{subcategory.id_subcategoria}</td>
        <td>{subcategory.subcategoria}</td>
        <td
          style={{
            display: 'flex',
          }}
        >
          <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
            <CustomButton
              color="orange"
              onClick={() => {
                setSelectedSubcategoryId(subcategory.id_subcategoria)
                setIsModalUpdateOpen(true)
              }}
            >
              <PencilFill />
            </CustomButton>
          </OverlayTrigger>

          <OverlayTrigger overlay={<Tooltip>Deletar</Tooltip>}>
            <CustomButton
              color="red"
              isLoading={isDeleteRequestLoading}
              onClick={() =>
                handleDeleteSubcategory(subcategory.id_subcategoria)
              }
            >
              <TrashFill />
            </CustomButton>
          </OverlayTrigger>
        </td>
      </tr>

      {isModalUpdateOpen && (
        <ModalUpdate
          subcategoryId={selectedSubcategoryId}
          isOpen={isModalUpdateOpen}
          onClose={() => setIsModalUpdateOpen(false)}
          loadSubcategories={loadSubcategories}
        />
      )}
    </>
  )
}
